import React from "react";
import { motion } from "framer-motion";
import { Check, Sparkles, Film, FileDown, Globe, Youtube, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { useRegion, isIndia } from "../contexts/RegionContext";
import { setIntent } from "../lib/nav";
import { ARTIST_TIERS } from "../data/content";

const ArtistSpotlight = () => {
    const { region } = useRegion();
    const inIndia = isIndia(region);

    return (
        <section id="spotlight" className="relative py-24 lg:py-32" data-testid="artist-spotlight">
            <div className="absolute inset-0 pointer-events-none">
                <div
                    className="absolute right-0 top-0 w-[34rem] h-[34rem] rounded-full opacity-[0.07] blur-3xl"
                    style={{
                        background:
                            "radial-gradient(circle, #D4A857 0%, transparent 60%)",
                    }}
                />
            </div>

            <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
                    <div>
                        <div className="pill"><span className="accent-dot" />For artists & performers</div>
                        <h2 className="mt-5 font-display text-5xl lg:text-6xl text-ivory tracking-tight">
                            IndoBlitz <span className="italic text-gold-shimmer">Spotlight.</span>
                        </h2>
                        <p className="mt-4 font-display italic text-xl lg:text-2xl text-ivory/70 leading-snug max-w-2xl">
                            IMDb-style portfolio websites for actors, musicians, filmmakers and performers - with one-click PDF press kits.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3 text-[11px] font-mono uppercase tracking-widest text-ivory/55">
                        <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 bg-white/[0.03]">
                            <Youtube className="w-3.5 h-3.5 text-gold/80" /> Reels embed
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 bg-white/[0.03]">
                            <Instagram className="w-3.5 h-3.5 text-gold/80" /> Social grid
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 bg-white/[0.03]">
                            <FileDown className="w-3.5 h-3.5 text-gold/80" /> PDF export
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 bg-white/[0.03]">
                            <Globe className="w-3.5 h-3.5 text-gold/80" /> Custom domain
                        </span>
                    </div>
                </div>

                <div className="grid lg:grid-cols-3 gap-5">
                    {ARTIST_TIERS.map((tier, i) => {
                        const price = inIndia ? tier.india : tier.global;
                        const symbol = inIndia ? "₹" : "$";
                        return (
                            <motion.div
                                key={tier.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.55, delay: i * 0.08 }}
                                className="relative"
                                data-testid={`artist-tier-${tier.id}`}
                            >
                                {tier.highlight && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20">
                                        <span className="gold-pulse inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold text-obsidian text-[10px] font-bold uppercase tracking-widest whitespace-nowrap">
                                            <Sparkles className="w-3 h-3" />
                                            Best value
                                        </span>
                                    </div>
                                )}

                                <div
                                    className={`relative rounded-2xl p-7 lg:p-8 ${tier.highlight
                                        ? "foil-sheen foil-border bg-gradient-to-b from-[#1A1408] via-[#0F0F14] to-obsidian shadow-[0_30px_80px_-30px_rgba(212,168,87,0.4)]"
                                        : "glass glass-hover"
                                    }`}
                                >
                                    <div className={`${tier.highlight ? "relative z-[1]" : ""}`}>
                                        <div className="flex items-center justify-between mb-4">
                                            <Film className="w-5 h-5 text-gold" />
                                            <span className="font-mono text-[10px] uppercase tracking-widest text-ivory/40">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                        </div>
                                        <h3 className={`font-display text-2xl lg:text-3xl ${tier.highlight ? "text-gold-shimmer" : "text-ivory"}`}>{tier.name}</h3>
                                        <p className="mt-2 text-ivory/55 text-sm">{tier.tagline}</p>

                                        <div className="mt-6 flex items-baseline gap-2">
                                            <span className="font-display text-4xl text-ivory tracking-tight">
                                                {symbol}{price.amount}
                                            </span>
                                            <span className="text-[10px] uppercase tracking-widest text-ivory/40 font-mono">{price.suffix}</span>
                                        </div>

                                        <ul className="mt-7 space-y-2.5">
                                            {tier.features.map((f) => (
                                                <li key={f} className="flex items-start gap-2 text-sm text-ivory/80">
                                                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${tier.highlight ? "text-gold" : "text-teal"}`} />
                                                    <span>{f}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        <Link
                                            to="/contact"
                                            onClick={() => setIntent("artist-spotlight")}
                                            className={`mt-7 w-full ${tier.highlight ? "btn-gold" : "btn-ghost"} block text-center !py-2.5 text-sm`}
                                            data-testid={`artist-cta-${tier.id}`}
                                        >
                                            Brief us about your work
                                        </Link>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                <p className="mt-10 text-center text-xs text-ivory/40 font-mono uppercase tracking-widest">
                    Every Spotlight site ships with shareable URL + downloadable PDF press kit · refresh-as-you-grow add-ons available
                </p>
            </div>
        </section>
    );
};

export default ArtistSpotlight;
